import React from "react";
import { useTranslation } from "react-i18next";
import { ImageIcon } from "lucide-react";
import { SERVICES_PROGRESS_COLOR } from "../services/constants";
import type { OperationsUnderestimatedServiceRow } from "./types";

export function UnderestimatedRateBadge({ rate }: { rate: number }) {
  return (
    <span className="inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full border border-[#E5E5E5] bg-white px-1 text-xs font-medium leading-none text-[#171717]">
      {rate}
    </span>
  );
}

function UnderestimatedServiceThumbnail({
  row,
}: {
  row: OperationsUnderestimatedServiceRow;
}) {
  const [hasError, setHasError] = React.useState(false);

  if (!row.imageUrl || hasError) {
    return (
      <span className="flex size-10 shrink-0 items-center justify-center rounded-lg border border-[#E5E5E5] bg-[#FAFAFA] text-[#A3A3A3]">
        <ImageIcon className="size-4" strokeWidth={1.5} />
      </span>
    );
  }

  return (
    <img
      src={row.imageUrl}
      alt=""
      className="size-10 shrink-0 rounded-lg object-cover"
      onError={() => setHasError(true)}
    />
  );
}

export function UnderestimatedServiceNameCell({
  row,
}: {
  row: OperationsUnderestimatedServiceRow;
}) {
  return (
    <div className="mx-auto flex w-full max-w-[120px] flex-col items-center justify-center gap-0.5">
      <UnderestimatedServiceThumbnail row={row} />
      <span className="line-clamp-2 w-full min-w-0 text-center text-[10px] font-normal leading-none text-[#0A0A0A]">
        {row.name}
      </span>
    </div>
  );
}

export function LatePercentCell({ percent }: { percent: number }) {
  return (
    <div className="mx-auto flex w-full max-w-[140px] items-center justify-center gap-3">
      <div className="h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-[#F4F4F5]">
        <div
          className="h-full rounded-full"
          style={{
            width: `${percent}%`,
            backgroundColor: SERVICES_PROGRESS_COLOR,
          }}
        />
      </div>
      <span className="w-9 shrink-0 text-center text-sm text-[#171717]">
        {percent} %
      </span>
    </div>
  );
}

export function AvgDelayCell({ delay }: { delay: string }) {
  return (
    <span className="text-[10px] font-normal leading-none text-red-600">
      {delay}
    </span>
  );
}

export function LateRequestsCell({
  late,
  total,
}: {
  late: number;
  total: number;
}) {
  const { t } = useTranslation();

  return (
    <span className="text-[10px] font-normal leading-none">
      <span className="text-red-600">{late}</span>
      <span className="text-[#171717]">
        {t("analytics.common.of", " of ")}
        {total}
      </span>
    </span>
  );
}
